/**
 * TeamLeader Exam Management Workspace — My Audit History page.
 *
 * A read-only, paginated log of exam-management actions the caller (this TeamLeader) has
 * personally performed — resets, retakes, reopens, force-submits, and bulk messages sent.
 * Backed by `GET /teamleader/exams/audit-history`, which the server always scopes to
 * "caller's own actions only" (no client-supplied user filter is honored).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTlAuditHistory } from "@/hooks/use-tl-exams";
import type { TlActivityLogItem, TlAuditHistoryParams } from "@/lib/api/tl-exams";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { PaginationControls } from "@/components/PaginationControls";
import { TableSkeleton } from "@/components/loading/TableSkeleton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { History, Search, FilterX, RotateCcw, CheckCircle2, XCircle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/exams_/audit-history")({
  component: TeamLeaderAuditHistoryPage,
});

// ── Static option sets ───────────────────────────────────────────────────────
// Mirrors LMSAPI.DAL.Enums.AuditActionCategory (backend enum, serialized by name) — restricted
// to the values actually emitted by TeamLeaderExamService/ExamService's TeamLeader-facing write
// paths (reset, retake, the reattempt/reset-content operations and force-submit all log
// ActionType=ExamEvent; bulk message logs
// NotificationSent; the CSV export logs ExportRun). Other enum members exist backend-side but
// are never written by this workspace's own actions, so they're omitted here.
const ACTION_TYPE_OPTIONS = [
  { value: "ExamEvent", label: "Exam Event" },
  { value: "NotificationSent", label: "Notification Sent" },
  { value: "ExportRun", label: "Export Run" },
] as const;

const ACTION_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ACTION_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

// Mirrors the literal EntityType strings passed to ActivityLogEntryDto at the TeamLeaderExamService/
// ExamService call sites for the scoped attempt actions and bulk-retake (ExamSession,
// ExamRetakeGrant), bulk message (BulkMessage), and export (Exam).
const ENTITY_TYPE_OPTIONS = ["ExamSession", "ExamRetakeGrant", "BulkMessage", "Exam"] as const;

// ── Formatting helpers (mirrors exams.tsx's local formatters — no shared util exists) ────────
function formatTimestamp(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Debounce a rapidly-changing value (search input) so each keystroke doesn't trigger a
// server round-trip — mirrors useDebouncedValue in exams.tsx (no shared hook exists).
function useDebouncedValue<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

// ── Page ──────────────────────────────────────────────────────────────────────
function TeamLeaderAuditHistoryPage() {
  const pagination = usePaginationState({ initialPageSize: 25 });

  // Raw (immediate) search input, debounced before it drives the query / pagination state.
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  useEffect(() => {
    pagination.setSearch(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const [actionTypeFilter, setActionTypeFilter] = useState("all");
  const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const setAndReset =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      pagination.reset();
    };

  const hasActiveFilters =
    actionTypeFilter !== "all" ||
    entityTypeFilter !== "all" ||
    !!dateFrom ||
    !!dateTo ||
    !!searchInput;

  const clearFilters = () => {
    setSearchInput("");
    setActionTypeFilter("all");
    setEntityTypeFilter("all");
    setDateFrom("");
    setDateTo("");
    pagination.reset();
  };

  // The audit-history endpoint's params diverge from TlExamListParams: `page` (not
  // `pageNumber`) and `sortDesc: boolean` (not `sortDir`). Build the query object with the
  // real param names rather than spreading usePaginationState()'s pageNumber/sortDir shape.
  const params: TlAuditHistoryParams = {
    search: pagination.search || undefined,
    actionType: actionTypeFilter !== "all" ? actionTypeFilter : undefined,
    entityType: entityTypeFilter !== "all" ? entityTypeFilter : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page: pagination.pageNumber,
    pageSize: pagination.pageSize,
    sortDesc: true,
  };

  const { data, isLoading, isError, refetch, isFetching } = useTlAuditHistory(params);
  const entries: TlActivityLogItem[] = data?.items ?? [];

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary shadow-inner">
              <History className="h-6 w-6" />
            </div>
            My Audit History
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            A record of exam-management actions you've performed — resets, retakes, reopens,
            force-submits, and messages sent.
          </p>
        </div>
      </div>

      {/* ── Filters ── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search actions..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={actionTypeFilter} onValueChange={setAndReset(setActionTypeFilter)}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All action types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All action types</SelectItem>
                {ACTION_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={entityTypeFilter} onValueChange={setAndReset(setEntityTypeFilter)}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All entity types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entity types</SelectItem>
                {ENTITY_TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2">
                <FilterX className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-xs font-medium text-muted-foreground">Date range:</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setAndReset(setDateFrom)(e.target.value)}
              className="w-40"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setAndReset(setDateTo)(e.target.value)}
              className="w-40"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4 text-primary" />
                Actions
              </CardTitle>
              <CardDescription>
                {data ? `${data.totalCount} action${data.totalCount === 1 ? "" : "s"}` : "Loading…"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={8} columns={6} />
            </div>
          ) : isError ? (
            <div className="p-10 text-center space-y-3">
              <p className="text-sm text-muted-foreground">Failed to load audit history.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              <History className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
              No actions recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[160px]">Timestamp</TableHead>
                    <TableHead>Action Type</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead>Entity Name / Id</TableHead>
                    <TableHead>Success</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-muted-foreground">
                        {formatTimestamp(entry.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {ACTION_TYPE_LABELS[entry.actionType] ?? entry.actionType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.entityType || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.entityName || entry.entityId || "—"}
                      </TableCell>
                      <TableCell>
                        {entry.isSuccess ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-success/15 text-success border-success/30 gap-1"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Success
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-destructive/15 text-destructive border-destructive/30 gap-1"
                          >
                            <XCircle className="h-3 w-3" />
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.ipAddress || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {data && (
            <div className="px-2">
              <PaginationControls
                pageNumber={data.pageNumber}
                pageSize={data.pageSize}
                totalPages={data.totalPages}
                totalCount={data.totalCount}
                hasNextPage={data.hasNextPage}
                hasPreviousPage={data.hasPreviousPage}
                onPageChange={pagination.setPageNumber}
                onPageSizeChange={pagination.setPageSize}
                isLoading={isFetching}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
